import React, { useState } from 'react';
import UserInputPanel from './components/UserInputPanel';
import ResultsPanel from './components/ResultsPanel';
import SavedScenarios from './components/SavedScenarios';
import { User, TransportMode, OptimizeResponse, SavedScenario } from './types';
import { optimizeRoute } from './services/api';
import { saveScenario } from './services/storage';
import './App.css';

const defaultUsers: User[] = [
  {
    id: 'user-1',
    name: 'Persona 1',
    origin: '',
    availableModes: ['driving', 'transit'] as TransportMode[],
  },
  {
    id: 'user-2',
    name: 'Persona 2',
    origin: '',
    availableModes: ['driving', 'transit'] as TransportMode[],
  },
];

const App: React.FC = () => {
  const [users, setUsers] = useState<User[]>(defaultUsers);
  const [destination, setDestination] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<OptimizeResponse | null>(null);

  const handleOptimize = async () => {
    // Validation
    const emptyOrigins = users.filter(u => !u.origin.trim());
    if (emptyOrigins.length > 0) {
      setError('Inserisci la posizione di partenza per tutti i partecipanti');
      return;
    }
    if (!destination.trim()) {
      setError('Inserisci la destinazione');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await optimizeRoute({ users, destination });
      setResult(response);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Errore durante il calcolo. Riprova.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSave = () => {
    const name = window.prompt('Nome dello scenario:', `${destination} - ${new Date().toLocaleDateString('it-IT')}`);
    if (name) {
      saveScenario(name, users, destination);
    }
  };

  const handleLoadScenario = (scenario: SavedScenario) => {
    setUsers(scenario.users);
    setDestination(scenario.destination);
    setResult(null);
    setError(null);
  };

  return (
    <div className="app">
      <header className="app-header">
        <h1>
          <span className="logo-icon">🗺️</span> Optimize Meet
        </h1>
        <p className="app-subtitle">
          Trova il modo migliore per incontrarvi
        </p>
      </header>

      <main className="app-main">
        <SavedScenarios onLoad={handleLoadScenario} />

        <UserInputPanel users={users} onUsersChange={setUsers} />

        <div className="destination-section">
          <h2>📍 Destinazione</h2>
          <input
            type="text"
            className="text-input destination-input"
            value={destination}
            onChange={e => setDestination(e.target.value)}
            placeholder="Es: Serravalle Scrivia Designer Outlet"
          />
        </div>

        <div className="action-buttons">
          <button
            className="btn-primary"
            onClick={handleOptimize}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner" /> Calcolo in corso...
              </>
            ) : (
              '🚀 Calcola percorso ottimale'
            )}
          </button>

          {(destination || users.some(u => u.origin)) && (
            <button
              type="button"
              className="btn-secondary"
              onClick={handleSave}
            >
              💾 Salva scenario
            </button>
          )}
        </div>

        {error && (
          <div className="error-banner" role="alert">
            ❌ {error}
          </div>
        )}

        {result && <ResultsPanel result={result} />}
      </main>

      <footer className="app-footer">
        <p>
          Optimize Meet — Powered by Google Maps API & AI
        </p>
      </footer>
    </div>
  );
};

export default App;
